// Pops up whenever a device holds an action for the owner's approval
// (docs/protocol.md "Approval round-trip"). Unanswered requests expire on the
// device (rejected), so this is modal and shows the countdown.

import type { ApprovalInfo } from "@fistos/shared/api";
import { ShieldAlert } from "lucide-solid";
import { createMemo, createSignal, Show } from "solid-js";
import { toast } from "somoto";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogPortal,
  DialogTitle,
} from "@/components/ui/dialog";
import { TextField, TextFieldInput } from "@/components/ui/text-field";
import { api, errorText } from "@/lib/api";
import { now } from "@/lib/clock";
import { humanize } from "@/lib/format";
import { live } from "@/lib/live";

export function pendingApprovals(): ApprovalInfo[] {
  const t = now();
  return Object.values(live.approvals)
    .filter((a) => a.status === "pending" && a.expiresAt > t)
    .sort((a, b) => a.createdAt - b.createdAt);
}

export function ApprovalPrompt() {
  const pending = createMemo(pendingApprovals);
  const current = () => pending()[0];
  const [dismissed, setDismissed] = createSignal<string | null>(null);
  const [note, setNote] = createSignal("");
  const [busy, setBusy] = createSignal(false);
  const open = () => !!current() && dismissed() !== current()!.id;

  const decide = async (approved: boolean) => {
    const a = current();
    if (!a) return;
    setBusy(true);
    try {
      await api.decideApproval(a.id, approved, note());
      setNote("");
      toast.success(approved ? "Approved" : "Declined");
    } catch (e) {
      toast.error(errorText(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open()} onOpenChange={(o) => !o && setDismissed(current()?.id ?? null)}>
      <DialogPortal>
        <DialogContent class="sm:max-w-md">
          <Show when={current()}>
            {(a) => {
              const left = () => Math.max(0, Math.round((a().expiresAt - now()) / 1000));
              const total = () => Math.max(1, Math.round((a().expiresAt - a().createdAt) / 1000));
              const device = () => live.devices[a().deviceId];
              return (
                <>
                  <DialogHeader>
                    <div class="bg-warning/15 text-warning mb-1 flex size-10 items-center justify-center rounded-full">
                      <ShieldAlert class="size-5" />
                    </div>
                    <DialogTitle>Approve: {humanize(a().category)}?</DialogTitle>
                    <DialogDescription>
                      {device()?.name ?? "A device"} {a().taskId ? "is running a task and" : ""} wants to do something
                      that needs your approval.
                    </DialogDescription>
                  </DialogHeader>
                  <div class="bg-muted/50 space-y-2 rounded-lg border p-3 text-sm">
                    <div class="font-mono text-[13px] break-words">{a().actionText}</div>
                    <div class="text-muted-foreground">{a().reason}</div>
                    <Show when={a().packageName}>
                      <div class="text-muted-foreground text-xs">In {a().packageName}</div>
                    </Show>
                  </div>
                  <div class="space-y-1">
                    <div class="text-muted-foreground flex justify-between text-xs">
                      <span>Declined automatically in</span>
                      <span class="tabular-nums">{left()} s</span>
                    </div>
                    <div class="bg-muted h-1 overflow-hidden rounded-full">
                      <div
                        class="bg-warning h-full transition-[width] duration-1000 ease-linear"
                        style={{ width: `${(left() / total()) * 100}%` }}
                      />
                    </div>
                  </div>
                  <TextField value={note()} onChange={setNote}>
                    <TextFieldInput placeholder="Note to the device (optional)" />
                  </TextField>
                  <Show when={pending().length > 1}>
                    <p class="text-muted-foreground text-xs">{pending().length - 1} more waiting after this one.</p>
                  </Show>
                  <DialogFooter>
                    <Button variant="outline" disabled={busy()} onClick={() => decide(false)}>
                      Decline
                    </Button>
                    <Button disabled={busy()} onClick={() => decide(true)}>
                      Approve
                    </Button>
                  </DialogFooter>
                </>
              );
            }}
          </Show>
        </DialogContent>
      </DialogPortal>
    </Dialog>
  );
}
